import { Text } from "@ttc3k/trekker";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { CollapsableStep } from "widgets/booker/shared";
import { useTranslation } from "react-i18next";
import {
  formatPrice,
  getEarlyPurchaseWindow,
  getMultilingualString,
  getResolvedProductSellingConfig,
  isSellingOptionSoldOut,
} from "shared/utils";
import { isTierVisibleForAnswers } from "shared/utils/dynamicFeeEval";
import {
  SelectableRadioCard,
  SelectableRadioCardGroup,
} from "shared/components";
import { Flex } from "styled-system/jsx";
import { EnumProductType, useSurveyByIdQuery } from "gql/generated";
import { useEffect, useMemo, useRef } from "react";
import _ from "lodash";
import { findProductTierIdForTag } from "shared/utils/membershipIntent";
import {
  areRequiredDynamicFeeQuestionsAnswered,
  getActiveDynamicFeeQuestions,
  getDynamicFeeSurveyId,
  getPricingConditionQuestionIds,
} from "../DynamicFeeQuestionnaire";

const getIntervalLabel = (months: number, t: (key: string) => string) => {
  if (months === 1) return t("PER_MONTH");
  if (months === 12) return t("PER_YEAR");
  return t("PER_N_MONTHS").replace("{{n}}", String(months));
};

export const TierSelection = () => {
  const { product, readOnlySelection, language, membershipIntent } =
    useBookerContext();
  const {
    selectedTierId,
    updateSelectedTier,
    questionnaireResponses,
    renewableFeeQuote,
  } = useCartContext();
  const { t } = useTranslation("booker", { keyPrefix: "TIER_SELECTION" });

  const sellingConfig = getResolvedProductSellingConfig(
    product as typeof product & { sellingConfig?: any },
  );
  const tiers = sellingConfig.options;
  const isRenewable = product?.type === EnumProductType.RenewableFee;
  const billingIntervalMonths =
    sellingConfig.recurring?.billingIntervalMonths ?? 1;
  const earlyPurchaseWindow = isRenewable
    ? getEarlyPurchaseWindow(sellingConfig.recurring)
    : null;
  // A known member renewing ahead isn't getting free days.
  const showEarlyPurchaseNote =
    !!earlyPurchaseWindow &&
    (!renewableFeeQuote.data ||
      renewableFeeQuote.data.pricingKind === "EARLY_PURCHASE");
  const dynamicFeeSurveyId = getDynamicFeeSurveyId(product as any);
  const hasDynamicFees = !!dynamicFeeSurveyId;

  const { data: dynamicFeeSurveyData, loading: dynamicFeeSurveyLoading } =
    useSurveyByIdQuery({
      variables: { id: dynamicFeeSurveyId ?? "" },
      skip: !dynamicFeeSurveyId,
    });
  const pricingQuestionIds = useMemo(
    () => getPricingConditionQuestionIds(tiers),
    [tiers],
  );
  const dynamicFeeQuestions = useMemo(
    () =>
      getActiveDynamicFeeQuestions(
        dynamicFeeSurveyData?.surveyById,
        pricingQuestionIds,
      ),
    [dynamicFeeSurveyData?.surveyById, pricingQuestionIds],
  );
  const isDynamicFeeQuestionnaireComplete =
    !hasDynamicFees ||
    (!dynamicFeeSurveyLoading &&
      areRequiredDynamicFeeQuestionsAnswered(
        dynamicFeeQuestions,
        questionnaireResponses,
      ));

  const answers = useMemo(
    () =>
      questionnaireResponses.map(r => ({
        questionId: r.questionData?._id ?? "",
        values: _.compact(r.response ?? []),
      })),
    [questionnaireResponses],
  );
  const hasAnsweredDynamicFeeQuestion = useMemo(() => {
    const questionIds = new Set(dynamicFeeQuestions.map(q => q._id));
    return answers.some(
      a =>
        questionIds.has(a.questionId) &&
        a.values.some(v => v.trim().length > 0),
    );
  }, [answers, dynamicFeeQuestions]);

  const sortedTiers = useMemo(
    () =>
      [...tiers]
        .filter(tier => tier.isActive !== false || tier._id === selectedTierId)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [tiers, selectedTierId],
  );
  const filteredTiers = useMemo(
    () =>
      hasDynamicFees
        ? sortedTiers.filter(tier =>
            isTierVisibleForAnswers(tier.condition ?? null, answers),
          )
        : sortedTiers,
    [hasDynamicFees, sortedTiers, answers],
  );

  useEffect(() => {
    if (readOnlySelection) return;
    if (hasDynamicFees && !isDynamicFeeQuestionnaireComplete) {
      if (selectedTierId) updateSelectedTier("");
      return;
    }
    const currentStillVisible = filteredTiers.some(
      t => t._id === selectedTierId && !isSellingOptionSoldOut(t),
    );
    if (selectedTierId && !currentStillVisible) {
      updateSelectedTier("");
    }
    const availableTiers = filteredTiers.filter(
      t => !isSellingOptionSoldOut(t),
    );
    if (
      availableTiers.length === 1 &&
      availableTiers[0]._id !== selectedTierId
    ) {
      updateSelectedTier(availableTiers[0]._id);
    }
  }, [
    readOnlySelection,
    hasDynamicFees,
    isDynamicFeeQuestionnaireComplete,
    selectedTierId,
    filteredTiers,
    updateSelectedTier,
  ]);

  const currentTierId = useMemo(
    () =>
      membershipIntent?.kind === "JOIN"
        ? null
        : findProductTierIdForTag(tiers, membershipIntent?.currentTierTagId),
    [tiers, membershipIntent],
  );
  const targetTierId = useMemo(
    () => findProductTierIdForTag(tiers, membershipIntent?.targetTierTagId),
    [tiers, membershipIntent],
  );
  const preselectTierId = targetTierId ?? currentTierId;
  // Preselect once; after that the member is free to pick another tier.
  const didPreselectTier = useRef(false);
  useEffect(() => {
    if (readOnlySelection || didPreselectTier.current) return;
    if (!preselectTierId || selectedTierId) return;
    const tier = filteredTiers.find(t => t._id === preselectTierId);
    if (!tier || isSellingOptionSoldOut(tier)) return;
    didPreselectTier.current = true;
    updateSelectedTier(preselectTierId);
  }, [
    readOnlySelection,
    preselectTierId,
    selectedTierId,
    filteredTiers,
    updateSelectedTier,
  ]);

  if (tiers.length === 0) return null;

  const visibleTiers = readOnlySelection
    ? filteredTiers.filter(tier => tier._id === selectedTierId)
    : filteredTiers;

  if (visibleTiers.length === 0) {
    // Without a matching option the customer can't check out, so say why
    // instead of silently leaving the Pay button disabled.
    if (!hasDynamicFees || readOnlySelection || dynamicFeeSurveyLoading) {
      return null;
    }
    return (
      <CollapsableStep
        title={t("TITLE")}
        complete={false}
        stepId="tier-selection"
      >
        <Text color="text.mid">
          {hasAnsweredDynamicFeeQuestion && isDynamicFeeQuestionnaireComplete
            ? t("NO_MATCHING_OPTION")
            : t("ANSWER_QUESTIONNAIRE_FIRST")}
        </Text>
      </CollapsableStep>
    );
  }

  if (readOnlySelection && visibleTiers.length <= 1) return null;

  // When there's only one pricing option, it's auto-selected above, so there's
  // no decision to make: hide the "Make your selection" header and helper copy.
  const singleOption =
    !readOnlySelection &&
    isDynamicFeeQuestionnaireComplete &&
    filteredTiers.length === 1;

  const stepTitle =
    membershipIntent?.kind === "RENEW" || membershipIntent?.kind === "CHANGE_TIER"
      ? t(`TITLE_${membershipIntent.kind}`)
      : t("TITLE");

  const tierCards = visibleTiers.map(tier => {
    const soldOut = isSellingOptionSoldOut(tier);
    const priceLabel =
      tier.price === 0
        ? t("FREE")
        : isRenewable
          ? `$${formatPrice(tier.price)} ${getIntervalLabel(billingIntervalMonths, t)}`
          : `$${formatPrice(tier.price)}`;

    return (
      <SelectableRadioCard
        key={tier._id}
        title={
          tier._id === currentTierId
            ? `${getMultilingualString(tier.name)} · ${t("CURRENT_TIER")}`
            : getMultilingualString(tier.name)
        }
        subtitleProps={{
          children: soldOut ? t("SOLD_OUT") : priceLabel,
          visual: "smallSemiBold",
        }}
        radioValue={tier._id}
        selectedValue={selectedTierId ?? ""}
        selectionIndicator={readOnlySelection ? "none" : "radio"}
        disabled={
          readOnlySelection ||
          soldOut ||
          (hasDynamicFees && !isDynamicFeeQuestionnaireComplete)
        }
      />
    );
  });

  return (
    <CollapsableStep
      title={stepTitle}
      complete={!!selectedTierId}
      hideHeader={singleOption}
      stepId="tier-selection"
    >
      {!readOnlySelection && !hasDynamicFees && !singleOption && (
        <Text color="text.mid">{t("SUBTITLE")}</Text>
      )}
      {!readOnlySelection &&
        hasDynamicFees &&
        !isDynamicFeeQuestionnaireComplete && (
          <Text color="text.mid">{t("ANSWER_QUESTIONNAIRE_FIRST")}</Text>
        )}
      {readOnlySelection ? (
        <Flex flexDirection="column" gap="150">
          {tierCards}
        </Flex>
      ) : (
        <SelectableRadioCardGroup
          value={selectedTierId ?? ""}
          onValueChange={updateSelectedTier}
        >
          {tierCards}
        </SelectableRadioCardGroup>
      )}
      {showEarlyPurchaseNote && earlyPurchaseWindow && (
        <Text visual="smallRegular" color="text.mid">
          {t("EARLY_PURCHASE_NOTE", {
            periodEnd: earlyPurchaseWindow.periodEnd.toLocaleDateString(
              language,
              { year: "numeric", month: "long", day: "numeric" },
            ),
          })}
        </Text>
      )}
    </CollapsableStep>
  );
};
